"use client";
import folderImage from "@/assets/images/folder-image.png";
import { useAspirationDetail } from "@/components/parts/admin/aspiration/api";
import { useRegionNames } from "@/components/parts/regions/api";
import HeaderPage from "@/components/sections/landing/headerPage";
import { BreadcrumbSetItem } from "@/components/shared/layouts/myBreadcrumb";
import FieldValue from "@/components/shared/review/fieldValue";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { getStatusText, getStatusTextColor } from "@/lib/utils";
import { Download } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useParams } from "next/navigation";

const DetailAspirationHistoryPage = () => {
  const { id } = useParams();
  const { data } = useAspirationDetail(Number(id) || 0);
  const aspirationDetail = data?.data;
  const { district, regency, village } = useRegionNames(
    {
      district: aspirationDetail?.subDistrict,
      regency: aspirationDetail?.district,
      village: aspirationDetail?.village,
    },
    ["district", "regency", "village"]
  );
  return (
    <section>
      <BreadcrumbSetItem
        items={[
          {
            title: "Beranda",
            href: `/`,
          },
          {
            title: "Usulan",
            href: `/aspiration`,
          },
          {
            title: `Detail Usulan`,
          },
        ]}
      />
      <HeaderPage>DETAIL USULAN</HeaderPage>
      <div className="md:container md:my-12 p-6 space-y-6">
        {aspirationDetail?.status !== "Pending" && (
          <Card className="p-5 space-y-2">
            <FieldValue
              label="Status Usulan"
              variant="colon"
              labelClassName="w-32"
            >
              <span className="font-semibold text-yellow-500">
                <div
                  className={`text-sm flex items-center gap-2 ${getStatusTextColor(aspirationDetail?.status ?? "") || "text-gray-500"}`}
                >
                  {getStatusText(aspirationDetail?.status ?? "") ||
                    aspirationDetail?.status}
                </div>
              </span>
            </FieldValue>
            <FieldValue label="Catatan" variant="colon" labelClassName="w-32">
              {aspirationDetail?.note || "Tidak ada catatan"}
            </FieldValue>
          </Card>
        )}

        <Card className="p-5 md:space-y-4 space-y-2">
          <div className="flex items-center gap-4 md:mb-7 mb-4">
            <h3 className="text-primary-700 font-semibold text-xl md:text-3xl whitespace-nowrap">
              Informasi Data Diri
            </h3>
            <div className="flex-1">
              <Separator className="bg-primary-700" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:gap-6 gap-3">
            {aspirationDetail?.institution && (
              <FieldValue label="Perusahaan">
                {aspirationDetail?.institution.name}
              </FieldValue>
            )}
            <FieldValue label="Nama Lengkap">
              {aspirationDetail?.name}
            </FieldValue>
            <FieldValue label="Kabupaten">{regency}</FieldValue>
            <FieldValue label="Kecamatan">{district}</FieldValue>
            <FieldValue label="Kelurahan / Desa">{village}</FieldValue>
          </div>
        </Card>
        <Card className="p-5 md:space-y-4 space-y-2">
          <div className="flex items-center gap-4 md:mb-7 mb-4">
            <h3 className="text-primary-700 font-semibold text-xl md:text-3xl whitespace-nowrap">
              Anggota Dewan
            </h3>
            <div className="flex-1">
              <Separator className="bg-primary-700" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:gap-6 gap-3">
            <FieldValue label="Nama Anggota DPRD">
              {aspirationDetail?.anggota?.name}
            </FieldValue>
            <FieldValue label="Dapil">
              {aspirationDetail?.dapil?.name}
            </FieldValue>
            <FieldValue label="Partai">
              {aspirationDetail?.fraksi?.name ?? "-"}
            </FieldValue>
            <FieldValue label="Logo Partai">
              <Image
                src={
                  aspirationDetail?.anggota?.fraksi?.logo ||
                  "/assets/images/no-image.png"
                }
                alt="logo-partai"
                width={80}
                height={80}
                className="rounded-lg object-contain w-20 h-20"
              />
            </FieldValue>
          </div>
        </Card>
        <Card className="p-5 md:space-y-4 space-y-2">
          <div className="flex items-center gap-4 md:mb-7 mb-4">
            <h3 className="text-primary-700 font-semibold text-xl md:text-3xl whitespace-nowrap">
              Informasi Usulan
            </h3>
            <div className="flex-1">
              <Separator className="bg-primary-700" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:gap-6 gap-3">
            <FieldValue label="Kategori Usulan">
              {aspirationDetail?.categoryAspiration?.name}
            </FieldValue>
            <FieldValue label="Usulan">{aspirationDetail?.proposal}</FieldValue>
            <FieldValue label="Uraian Permasalahan" className="sm:col-span-2">
              {aspirationDetail?.description}
            </FieldValue>
            <FieldValue label="Volume">
              {aspirationDetail?.volume ?? "-"}
            </FieldValue>
            <div className="sm:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
              <iframe
                src={`https://maps.google.com/maps?q=${aspirationDetail?.latitude ?? "-"},${aspirationDetail?.longitude ?? "-"}&z=15&output=embed`}
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />

              <iframe
                title="Google Street View"
                width="100%"
                height="400"
                loading="lazy"
                allowFullScreen
                src={`https://www.google.com/maps?q=&layer=c&cbll=${aspirationDetail?.latitude},${aspirationDetail?.longitude}&cbp=11,0,0,0,0&output=svembed`}
              ></iframe>
            </div>
            <div className="sm:col-span-2 grid grid-cols-2">
              <FieldValue label="Latitude">
                {aspirationDetail?.latitude ?? "-"}
              </FieldValue>
              <FieldValue label="Longitude">
                {aspirationDetail?.longitude ?? "-"}
              </FieldValue>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
            {aspirationDetail?.imagesUrl.map((image, index) => (
              <Dialog key={index}>
                <DialogTrigger>
                  <Image
                    key={index}
                    src={
                      image && image.includes("https://newus-bucket")
                        ? image
                        : "/assets/images/no-image.png"
                    }
                    alt={`complain-image-${index}`}
                    width={200}
                    height={200}
                    className="rounded-lg object-cover border border-primary-700 w-40 h-40"
                  />
                </DialogTrigger>
                <DialogContent>
                  <DialogTitle>Detail Gambar</DialogTitle>

                  <Image
                    key={index}
                    src={
                      image && image.includes("https://newus-bucket")
                        ? image
                        : "/assets/images/no-image.png"
                    }
                    alt={`complain-image-${index}`}
                    width={1000}
                    height={1000}
                    className="rounded-lg sm:w-full object-contain sm:h-96"
                  />
                </DialogContent>
              </Dialog>
            ))}
          </div>
          {aspirationDetail?.dokumenUrl[0] && (
            <Link
              href={aspirationDetail?.dokumenUrl[0] ?? "/"}
              target="_blank"
              download
              className="flex items-center justify-between gap-4 mt-4 px-4 py-2 rounded-xl border border-gray-300"
            >
              <figure className="flex items-center gap-2 w-full overflow-hidden">
                <Image
                  src={folderImage}
                  alt="folder-image"
                  width={100}
                  height={100}
                  className="h-10 w-10 object-contain"
                />
                <figcaption className="line-clamp-2">
                  {aspirationDetail?.dokumenUrl[0]?.split("/")[5]}
                </figcaption>
              </figure>
              <div className="text-primary-700">
                <Download />
              </div>
            </Link>
          )}
        </Card>
      </div>
    </section>
  );
};

export default DetailAspirationHistoryPage;
